import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { ComponentProps, useCallback, useEffect, useState } from 'react';
import {
    CustomShippingRateModalPages,
    useCustomShippingRateModalPages,
} from '@/components/ShippingInfo/LiveShippingInfo/components/ShippingPickupOptions/components/LiveShipping/components/CustomShippingRateModal/hooks/useCustomShippingRateModalPages';
import {
    getShippingEstimateAvailability,
    getShippingEstimateHadError,
    isShippingEstimateLoading,
} from '@/redux/modules/shipping/shipping.selectors';
import { LIVE_AUCTIONEERS_PACKAGING_EMAIL } from '@/components/SupportEmailLink/constants/support.constants';
import { LIVE_SHIPPING_METHOD_TRANSLATION_KEY } from '@/components/ShippingInfo/LiveShippingInfo/components/ShippingPickupOptions/components/LiveShipping/LiveShippingMethodRow.constants';
import { sendCustomShippingRateRequest } from '@/redux/modules/shipping/shipping.actions';
import { SubtleLink } from '@liveauctioneers/hammer-ui-core/link';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import ContentPager, { Page } from '@liveauctioneers/caterwaul-components/lib/ContentPager/ContentPager';
import CustomShippingRateForm from '@/components/ShippingInfo/LiveShippingInfo/components/ShippingPickupOptions/components/LiveShipping/components/CustomShippingRateModal/components/CustomShippingRateForm/CustomShippingRateForm';
import CustomShippingRateSuccess from '@/components/ShippingInfo/LiveShippingInfo/components/ShippingPickupOptions/components/LiveShipping/components/CustomShippingRateModal/components/CustomShippingRateSuccess/CustomShippingRateSuccess';
import Modal from '@liveauctioneers/caterwaul-components/lib/SimpleModal/SimpleModal';
import ModalHeader from '@liveauctioneers/caterwaul-components/lib/SimpleModal/SimpleModalHeader';
import ShippingRateModalLocationForm from '@/components/ShippingInfo/LiveShippingInfo/components/ShippingPickupOptions/components/LiveShipping/components/CustomShippingRateModal/components/LocationForm/ShippingRateModalLocationForm';
import styled from 'styled-components';

export type CustomShippingRateModalProps = {
    isOpen: boolean;
    itemId: number;
    onClose: () => void;
    onSubmitAddressInformation: (countryCode: string, postalCode: string) => Promise<void>;
};

const CustomShippingRateModal = ({
    isOpen,
    itemId,
    onClose,
    onSubmitAddressInformation,
}: CustomShippingRateModalProps) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const isLoading = useAppSelector((state) => isShippingEstimateLoading(state, itemId));
    const succeeded = useAppSelector((state) => getShippingEstimateAvailability(state, itemId));
    const hadError = useAppSelector((state) => getShippingEstimateHadError(state, itemId));
    const { currentPage, goToNextPage } = useCustomShippingRateModalPages(itemId, isOpen);
    const [enteredPostalCode, setEnteredPostalCode] = useState('');
    const [enteredCountryCode, setEnteredCountryCode] = useState('');

    const onSubmitCalculationRequest: ComponentProps<typeof ShippingRateModalLocationForm>['onSubmit'] = useCallback(
        async (country, postalCode) => {
            setEnteredPostalCode(postalCode);
            setEnteredCountryCode(country);
            await onSubmitAddressInformation(country, postalCode);
        },
        [onSubmitAddressInformation]
    );

    const onSubmitEmailRequest: ComponentProps<typeof CustomShippingRateForm>['onSubmit'] = useCallback(
        async (postalCode, emailAddress, countryCode) => {
            await dispatch(
                sendCustomShippingRateRequest({
                    countryCode,
                    emailAddress,
                    itemId,
                    postalCode,
                })
            );
            goToNextPage();
        },
        [dispatch, goToNextPage, itemId]
    );

    useEffect(() => {
        if (hadError) {
            onClose();
        }
    }, [hadError, onClose]);

    useEffect(() => {
        if (succeeded && !isLoading) {
            onClose();
        }
    }, [isLoading, onClose, succeeded]);

    return (
        <Modal
            body={
                <>
                    <ContentPager currentPage={currentPage}>
                        <Page name={CustomShippingRateModalPages.CalculateQuoteForm}>
                            <ShippingRateModalLocationForm
                                onClose={onClose}
                                onSubmit={onSubmitCalculationRequest}
                            />
                        </Page>
                        <Page name={CustomShippingRateModalPages.TooExpensiveForm}>
                            <CustomShippingRateForm
                                itemId={itemId}
                                onSubmit={onSubmitEmailRequest}
                                providedCountryCode={enteredCountryCode}
                                providedPostalCode={enteredPostalCode}
                            />
                        </Page>
                        <Page name={CustomShippingRateModalPages.TooExpensiveRequestSuccess}>
                            <CustomShippingRateSuccess onClose={onClose} />
                        </Page>
                    </ContentPager>
                    <StyledQuestions>
                        <BodySecondary>
                            {formatMessage({
                                id: `${LIVE_SHIPPING_METHOD_TRANSLATION_KEY}.customShippingRateModal.questions`,
                            })}
                        </BodySecondary>
                        <SubtleLink
                            href={`mailto:${LIVE_AUCTIONEERS_PACKAGING_EMAIL}`}
                            size="sm"
                        >
                            {LIVE_AUCTIONEERS_PACKAGING_EMAIL}
                        </SubtleLink>
                    </StyledQuestions>
                </>
            }
            header={
                <ModalHeader
                    onClose={onClose}
                    subTitle={
                        // "enter your location" page does not have additional info
                        currentPage === CustomShippingRateModalPages.CalculateQuoteForm
                            ? ''
                            : formatMessage({
                                  id: `${LIVE_SHIPPING_METHOD_TRANSLATION_KEY}.customShippingRateModal.${currentPage}.additionalInfo`,
                              })
                    }
                    title={formatMessage({
                        id: `${LIVE_SHIPPING_METHOD_TRANSLATION_KEY}.customShippingRateModal.${currentPage}.title`,
                    })}
                />
            }
            onAfterClose={onClose}
            show={isOpen}
            testid="custom-shipping-rate-modal"
        />
    );
};

export default CustomShippingRateModal;

const StyledQuestions = styled.div`
    margin: 32px auto 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
`;
